/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams } from 'react-router';
import { WorkspaceItemTitle } from '../../items/WorkspaceItemTitle';
import type { RouteParams } from '@user/pages/RouteParams';
import { classNames } from '@framework/utils';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';
import { useAtomValue } from 'jotai';
import { searchResultsAtom, type WorkspaceJSON } from './trpcSearchResultState';
import { sidebarSearchKeywordAtom } from '../sidebarSearchKeywordAtom';
import { SearchFolderItem } from './SearchFolderItem';
import { SearchViewModelItem } from './SearchViewModelItem';

type Props = {
  workspace: WorkspaceJSON;
  rootFolderTree: RootFolderTree;
};

export const SearchWorkspaceItem: React.FC<Props> = ({ workspace, rootFolderTree }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { workspaceId: workspaceIdParam, folderId: folderIdParam } = useParams<RouteParams>();
  const isSelected = useMemo(
    () => !folderIdParam && workspace.id === workspaceIdParam,
    [workspace.id, workspaceIdParam, folderIdParam]
  );

  const trpcResults = useAtomValue(searchResultsAtom);

  const searchKeyword = useAtomValue(sidebarSearchKeywordAtom);

  useEffect(() => {
    if (searchKeyword) setIsOpen(true);
  }, [searchKeyword]);

  const toggleWorkspace = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const folders = useMemo(() => {
    return trpcResults.results.folders;
  }, [trpcResults]);

  const viewModels = useMemo(() => {
    return trpcResults.results.viewModels;
  }, [trpcResults]);

  const viewModelTags = useMemo(() => {
    return trpcResults.results.viewModelTags;
  }, [trpcResults]);

  const sortedFolders = useMemo(
    () =>
      rootFolderTree
        .getFolderIdsOfRoot()
        .map((folderId) => folders[folderId])
        .filter((folder) => !!folder)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [rootFolderTree, folders]
  );

  const sortedViewModels = useMemo(
    () =>
      rootFolderTree
        .getViewModelIdsOfRoot()
        .map((viewModelId) => viewModels[viewModelId])
        .filter((viewModel) => !!viewModel)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [rootFolderTree, viewModels]
  );

  return (
    <div>
      <div className="flex items-center">
        <div className="mr-1">
          <button className="focus:outline-none" onClick={toggleWorkspace}>
            <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} className="size-4" />
          </button>
        </div>
        <div className={classNames('grow', isSelected ? 'bg-gray-300 hover:bg-gray-400' : 'hover:bg-gray-300')}>
          <WorkspaceItemTitle
            workspaceId={workspace.id}
            workspaceName={workspace.name.toString()}
            isPersonalWorkspace={workspace.isPersonal}
            isPublicWorkspace={workspace.isPublicSpace}
            isInternalPublicWorkspace={workspace.isInternalPublic}
            searchKeyword={searchKeyword}
          />
        </div>
      </div>

      {isOpen && (
        <div className="ml-4">
          <>
            {sortedFolders.map((folder) => (
              <SearchFolderItem key={folder.id} workspace={workspace} folder={folder} rootFolderTree={rootFolderTree} />
            ))}
            {sortedViewModels.map((viewModel) => (
              <SearchViewModelItem
                key={viewModel.id}
                groupId={workspace.groupId}
                viewModel={viewModel}
                viewModelTags={viewModelTags[viewModel.id] ?? []}
                isIndividualPermissionEnabled={workspace.isIndividualPermissionEnabled}
              />
            ))}
          </>
        </div>
      )}
    </div>
  );
};
